'use client';
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable tailwindcss/no-custom-classname */
import { ComparisonTableProps } from '@hyphengroup/cms-components/cms-blocks/ComparisonTable/types';
import { ApplyCtaLink } from '@hyphengroup/cms-components/components/ApplyCta';
import Button from '@hyphengroup/cms-components/components/Button';
import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import { ProductCardOffer } from '@hyphengroup/cms-components/components/ProductCardOffer';
import { TheWrapper } from '@hyphengroup/cms-components/components/TheWrapper';
import Tooltip from '@hyphengroup/cms-components/components/Tooltip';
import {
  ComponentTypeEnum,
  UserActionEnum,
} from '@hyphengroup/cms-components/utils/analytics/sendAnalytics';
import { formatAttributeItemValue } from '@hyphengroup/cms-components/utils/helpers';
import classNames from 'classnames';
import React, {
  CSSProperties,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { sendProductAnalytics } from '../helpers/sendAnalytics';
import { BaseAttribute } from '$/(cms)/_modules/cmsService/types';
import { generateButtonSubText } from '$/components/cms/helpers';
import { CONTENT_MHG } from '$/components/cms/translations/mhg';
import { FeLink } from '$/components/FeLink';
import ArrowBottom from '$/components/icons/ArrowBottom';
import ArrowRightAlt from '$/components/icons/ArrowRightAlt';
import ArrowUp from '$/components/icons/ArrowUp';
import { useClientContext } from '$/modules/context/client';
import { sessionAttributionTracker } from '$/modules/sessionAttributionTracking/sessionAttributionTracker';
// import './productComparisonTableTravelInsurance.css';
const defaultActiveReward = -1;

type ProductComparisonTableTravelInsuranceProps = {
  products: ComparisonTableProps['products'];
  tableConfig: {
    attributes: ComparisonTableProps['tableConfig']['attributes'];
    titleReq: ComparisonTableProps['tableConfig']['titleReq'];
  };
};

export const ProductComparisonTableTravelInsurance: React.FC<
  ProductComparisonTableTravelInsuranceProps
> = (props) => {
  const {
    products,
    tableConfig: { attributes, titleReq },
  } = props;

  const { lang, brand } = useClientContext();
  const staticText = CONTENT_MHG.get(lang);

  const attribute_list = useMemo(
    () =>
      attributes?.reduce<Record<string, string>>((prev, current) => {
        if (!current?.attribute?.name || !current?.label) return prev;
        return {
          ...prev,
          [current.attribute.name]: current.label,
        };
      }, {}) ?? {},
    [attributes],
  );

  const attribute_list_additional = useMemo(
    () =>
      attributes?.reduce<Record<string, string>>((prev, current) => {
        if (!current?.attribute?.name || !current?.additional_information)
          return prev;
        return {
          ...prev,
          [current.attribute.name]: current.additional_information,
        };
      }, {}) ?? {},
    [attributes],
  );

  const [activeReward, setActiveReward] = useState(defaultActiveReward);
  const scrollable = useRef<HTMLDivElement>(null);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const isDragging = useRef(false);
  const startX = useRef<number>(0);
  const scrollLeft = useRef<number>(0);

  useEffect(() => {
    const scrollableRef = scrollable.current;

    if (!scrollableRef) {
      return;
    }

    const updateScrolledToEnd = () => {
      setIsScrolledToEnd(
        scrollableRef.scrollLeft + scrollableRef.clientWidth >=
          scrollableRef.scrollWidth,
      );
    };

    const mouseDownHandler: EventListener = (e: Event) => {
      const event = e as unknown as MouseEvent;
      isDragging.current = true;
      startX.current = event.pageX - scrollableRef!.offsetLeft;
      scrollLeft.current = scrollableRef!.scrollLeft;
      scrollableRef.style.cursor = 'grabbing';
    };

    const mouseLeaveHandler: EventListener = () => {
      isDragging.current = false;
      scrollableRef.style.cursor = 'grab';
    };

    const mouseUpHandler: EventListener = () => {
      isDragging.current = false;
      scrollableRef.style.cursor = 'grab';
    };

    const mouseMoveHandler: EventListener = (e: Event) => {
      if (!isDragging.current) {
        return;
      }
      const event = e as unknown as MouseEvent;
      const x = event.pageX - scrollableRef!.offsetLeft;
      const walk = x - startX.current;
      scrollableRef.scrollLeft = scrollLeft.current - walk;

      updateScrolledToEnd();
    };

    const scrollHandler: EventListener = () => {
      updateScrolledToEnd();
    };

    scrollableRef.addEventListener('mousedown', mouseDownHandler);
    scrollableRef.addEventListener('mouseleave', mouseLeaveHandler);
    scrollableRef.addEventListener('mouseup', mouseUpHandler);
    scrollableRef.addEventListener('mousemove', mouseMoveHandler);
    scrollableRef.addEventListener('scroll', scrollHandler);

    updateScrolledToEnd();

    return () => {
      scrollableRef.removeEventListener('scroll', scrollHandler);
      scrollableRef.removeEventListener('mousemove', mouseMoveHandler);
      scrollableRef.removeEventListener('mouseup', mouseUpHandler);
      scrollableRef.removeEventListener('mouseleave', mouseLeaveHandler);
      scrollableRef.removeEventListener('mousedown', mouseDownHandler);
    };
  }, [scrollable]);

  const handleOpenRewards = (rowNumber: number) => {
    setActiveReward((oldValue) =>
      oldValue === rowNumber ? defaultActiveReward : rowNumber,
    );
  };

  const handleCloseRewards = () => {
    setActiveReward(defaultActiveReward);
  };

  // We can keep the header and cell components only in FE side,
  // on the parent page, to be able to customize other tables easily
  const header = useMemo<string[]>(
    () => [
      staticText.provider,
      ...Object.values(attribute_list),
      staticText.offers_rewards,
      staticText.apply,
    ],
    [staticText, attribute_list],
  );

  const headerLength = useMemo(() => header.length, [header]);

  const getAttributeName = (attribute: BaseAttribute): string => {
    switch (attribute!.__typename) {
      case 'ComponentAttributesAttrRichTextEditor':
      case 'ComponentAttributesAttrNumberRange':
      case 'ComponentAttributesAttrNumber':
      case 'ComponentAttributesAttrBoolean':
      case 'ComponentAttributesAttrPlainText':
      case 'ComponentAttributesAttrKeyValue':
      case 'ComponentAttributesAttrJson': {
        return attribute!.name?.name ?? '';
      }
    }
    return attribute?.__typename ?? '';
  };

  const findByTypeAndConcatenate = (
    items: BaseAttribute[] | null | undefined,
    name: string,
  ) => {
    if (!items) return staticText.n_a;
    const item = items.find((item) => getAttributeName(item) === name);
    if (item) {
      const value = formatAttributeItemValue(item);
      return value ?? staticText.n_a;
    }
    return staticText.n_a;
  };

  const getAdditionalInformation = (items: BaseAttribute[], name: string) => {
    const item = items.find((item) => getAttributeName(item) === name);
    if (item) {
      switch (item.__typename) {
        case 'ComponentAttributesAttrNumberRange':
        case 'ComponentAttributesAttrNumber': {
          return item.additional_information ?? null;
        }
      }
    }
    return null;
  };

  const anchorLink = (name?: string) => {
    return name
      ?.trim()
      .toLowerCase()
      .replace(/[:(),[\]\s+&$]+/g, '-');
  };

  const scrollToAnchor = (name: string) => {
    const anchorId = anchorLink(name);
    if (typeof document === 'undefined') {
      return;
    }
    const element = document.querySelector(`#${anchorId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const wrapperStyleProperties = {
    '--header-length': headerLength,
  } as CSSProperties;

  return (
    <TheWrapper type="full" title={titleReq}>
      <div
        className={classNames(
          {
            'mr-[-1.5rem]': isScrolledToEnd,
            'mr-0': isScrolledToEnd,
          },
          'mb-6',
          'mt-6',
          'card-radius',
          'select-none',
          'lg:mr-0',
          'relative',
          'overflow-hidden',
          'shadow',
          'product-comparison-table-travel-insurance',
          'scrollTable',
        )}
      >
        <div
          className={classNames(
            'h-full',
            'w-12',
            'absolute',
            'top-0',
            'right-0',
            'z-50',
            { hidden: isScrolledToEnd },
          )}
          style={{
            background:
              'linear-gradient(90deg, rgba(255, 255, 255, 0) 30%, rgb(255, 255, 255) 60%)',
          }}
        />
        <div
          style={wrapperStyleProperties}
          ref={scrollable}
          className="table-container"
        >
          <table
            className="w-full min-w-[1024px] bg-white"
            style={{ display: headerLength ? 'displayTable' : '' }}
          >
            <thead className="contents text-left">
              <tr className="contents">
                {header.map((tab, index) => (
                  <th
                    key={tab}
                    className={`flex items-center p-4 bg-gray-100 alpha-overline xl:p-6 ${index >= header.length - 2 ? 'hidden lg:flex' : ''}`}
                  >
                    {tab}
                    {attribute_list_additional &&
                      attribute_list_additional[tab] && (
                        <Tooltip
                          content={attribute_list_additional[tab]}
                          holderClasses={['ml-1']}
                        />
                      )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="contents">
              {!products?.filter(Boolean).length && (
                <tr className="contents">
                  <td>{staticText.n_a}</td>
                </tr>
              )}
              {products?.filter(Boolean).map((product, rowIndex) => {
                const totalRewards =
                  (product?.exclusive_offers?.length ?? 0) +
                  (product?.welcome_offers?.length ?? 0);
                return (
                  <React.Fragment key={product?.uid}>
                    <tr className="table-line contents lg:hover:bg-gray-50">
                      {/* Image START */}
                      <td className="flex items-center border-t border-gray-300 p-4 bg-white xl:p-6">
                        <div className="flex flex-col sm:gap-4">
                          <div className="flex w-28 items-center justify-center">
                            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions,jsx-a11y/anchor-is-valid */}
                            <FeLink
                              href="#"
                              onClick={() =>
                                scrollToAnchor(product!.product_name!)
                              }
                            >
                              <CmsImage
                                unoptimized
                                src={product?.product_image ?? ''}
                                alt={product?.product_name ?? 'Product'}
                                height={75}
                                width={112}
                                className="max-h-[75px] w-auto cursor-pointer object-contain"
                              />
                            </FeLink>
                          </div>
                          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                          <FeLink
                            href={`#${anchorLink(product!.product_name)}`}
                            onClick={(e) => {
                              e.preventDefault();
                              scrollToAnchor(product!.product_name!);
                            }}
                            className="xs:max-w-50 !text-gray-900 alpha-body-2 hover:underline hover:!text-gray-800 sm:max-w-75 lg:max-w-full"
                          >
                            {product?.product_name}
                          </FeLink>
                        </div>
                      </td>
                      {/* Image END */}

                      {/* Attributes START */}
                      {Object.keys(attribute_list).map((key) => (
                        <td
                          key={key}
                          className="flex items-center border-t border-gray-300 p-4 bg-white xl:p-6"
                        >
                          <div className="col flex items-center gap-1">
                            <p className="whitespace-nowrap alpha-body-1 lg:whitespace-normal">
                              {findByTypeAndConcatenate(
                                product?.attributes ?? [],
                                key,
                              )}
                            </p>
                            {getAdditionalInformation(
                              product?.attributes ?? [],
                              key,
                            ) && (
                              <Tooltip
                                content={
                                  getAdditionalInformation(
                                    product?.attributes ?? [],
                                    key,
                                  ) ?? ''
                                }
                              />
                            )}
                          </div>
                        </td>
                      ))}
                      {/* Attributes END */}

                      {/* Rewards & button column START */}
                      <td className="offers-rewards hidden items-center border-t border-gray-300 p-4 bg-white lg:flex xl:p-6">
                        {totalRewards > 0 ? (
                          <>
                            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                            <p
                              onClick={() => handleOpenRewards(rowIndex)}
                              className="hidden cursor-pointer text-nowrap !font-semibold underline !text-[--buttons-primary] alpha-body-2 lg:inline-block"
                            >
                              {staticText.eligible_rewards} ({totalRewards})
                            </p>
                            <div className="inline-block alpha-body-2 lg:hidden">
                              <Button
                                onClick={() => handleOpenRewards(rowIndex)}
                                title={`${staticText.view_rewards} (${totalRewards})`}
                                color="text-[--buttons-primary]"
                              >{`${staticText.eligible_rewards} (${totalRewards})`}</Button>
                            </div>
                          </>
                        ) : (
                          <div>{staticText.n_a}</div>
                        )}
                      </td>
                      <td className="hidden items-center border-t border-gray-300 p-4 bg-white lg:flex xl:p-6">
                        {product?.call_to_action?.links?.[0]
                          ?.redirected_url && (
                          <div className="flex flex-auto flex-col gap-2">
                            <ApplyCtaLink
                              product={product}
                              e2eCTA={product.e2eToogleButton}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button
                                title={
                                  product?.call_to_action?.links?.[0]?.label
                                }
                                endIcon={<ArrowRightAlt className="size-5" />}
                                // classes="!w-full !min-w-[215px]"
                                className="w-full sm:w-54"
                                styleType="cta"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sessionAttributionTracker.recordApplyClick();
                                  sendProductAnalytics({
                                    action: UserActionEnum.CLICK,
                                    componentTitle: `${titleReq}_${product?.call_to_action?.links?.[0]?.label}`,
                                    componentType: ComponentTypeEnum.TABLE,
                                    event: 'product_click_apply',
                                    index: rowIndex.toString(),
                                    product: product,
                                    url: titleReq,
                                  });
                                }}
                              >
                                {product?.call_to_action?.links?.[0]?.label}
                              </Button>
                            </ApplyCtaLink>
                            <p className="text-center font-medium text-gray-700 alpha-body-2 leading-none">
                              {generateButtonSubText({
                                btnLabel:
                                  product?.call_to_action?.links?.[0]?.label,
                                appName: brand,
                                lang: lang,
                                providerName:
                                  product?.provider?.provider_name ?? '',
                              })}
                            </p>
                          </div>
                        )}
                      </td>
                      <td
                        colSpan={headerLength}
                        className="flex p-4 bg-white lg:hidden xl:p-6"
                      >
                        <div className="flex flex-row-reverse gap-2">
                          {totalRewards > 0 && (
                            <>
                              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                              <p
                                onClick={() => handleOpenRewards(rowIndex)}
                                className="hidden cursor-pointer text-nowrap !font-semibold underline !text-[--buttons-primary] alpha-body-2 lg:inline-block"
                              >
                                {staticText.eligible_rewards} ({totalRewards})
                              </p>
                              <div className="inline-block alpha-body-2 lg:hidden">
                                <Button
                                  onClick={() => handleOpenRewards(rowIndex)}
                                  title={`${staticText.view_rewards} (${totalRewards})`}
                                  variant="text"
                                  styleType="primary"
                                  className="text-primary-800"
                                  endIcon={<ArrowRightAlt className="size-5" />}
                                >
                                  {`${staticText.eligible_rewards} (${totalRewards})`}
                                </Button>
                              </div>
                            </>
                          )}
                          {product?.call_to_action?.links?.[0]
                            ?.redirected_url && (
                            <div className="flex flex-col gap-2">
                              <ApplyCtaLink
                                product={product}
                                e2eCTA={product.e2eToogleButton}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button
                                  title={
                                    product?.call_to_action?.links?.[0]?.label
                                  }
                                  endIcon={<ArrowRightAlt className="size-5" />}
                                  className="w-39 sm:w-41"
                                  styleType="cta"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    sessionAttributionTracker.recordApplyClick();
                                    sendProductAnalytics({
                                      action: UserActionEnum.CLICK,
                                      componentTitle: `${titleReq}_${product?.call_to_action?.links?.[0]?.label}`,
                                      componentType: ComponentTypeEnum.TABLE,
                                      event: 'product_click_apply',
                                      index: rowIndex.toString(),
                                      product: product,
                                    });
                                  }}
                                >
                                  {product?.call_to_action?.links?.[0]?.label}
                                </Button>
                              </ApplyCtaLink>
                              <p className="xs:max-w-[155px] text-center font-medium text-gray-700 alpha-body-2 leading-none sm:max-w-auto">
                                {generateButtonSubText({
                                  btnLabel:
                                    product?.call_to_action?.links?.[0]?.label,
                                  appName: brand,
                                  lang: lang,
                                  providerName:
                                    product?.provider?.provider_name ?? '',
                                })}
                              </p>
                            </div>
                          )}
                        </div>
                      </td>
                      {/* Rewards & button column END */}
                    </tr>
                    {/* Common table row END */}

                    {/* Expandable row START */}
                    {((product?.exclusive_offers?.length ?? 0) > 0 ||
                      (product?.welcome_offers?.length ?? 0) > 0) &&
                      activeReward === rowIndex && (
                        <tr className="contents">
                          <td
                            colSpan={headerLength}
                            className="relative flex p-4 bg-[--highlights-blue-light] xl:p-6"
                          >
                            <div className="flex w-full flex-col">
                              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                              <div
                                className="flex cursor-pointer justify-between"
                                onClick={() => handleCloseRewards()}
                              >
                                <p className="mb-6 text-[--buttons-primary] alpha-subheading-2">
                                  {staticText.eligible_rewards} ({totalRewards})
                                </p>
                                {/*<i dangerouslySetInnerHTML={{ __html: icons.arrow }}></i>*/}
                                {activeReward === rowIndex ? (
                                  <ArrowUp className="size-[--shapes-icon-size-lg]" />
                                ) : (
                                  <ArrowBottom className="size-[--shapes-icon-size-lg]" />
                                )}
                              </div>

                              {/* Rewards section START */}
                              <div className="flex flex-col gap-4 alpha-body-2">
                                {product?.exclusive_offers &&
                                  product?.exclusive_offers
                                    ?.filter(Boolean)
                                    ?.map((offer) => (
                                      // TODO: replace to ComparisonCardOfferTravelInsurance
                                      <ProductCardOffer
                                        key={offer.uid}
                                        {...offer}
                                      />
                                    ))}
                                {product?.welcome_offers &&
                                  product?.welcome_offers
                                    ?.filter(Boolean)
                                    ?.map((offer) => (
                                      // TODO: replace to ComparisonCardOfferTravelInsurance
                                      <ProductCardOffer
                                        key={offer.uid}
                                        {...offer}
                                      />
                                    ))}
                              </div>
                              {/* Rewards section END */}
                            </div>
                          </td>
                        </tr>
                      )}
                    {/* Expandable row END */}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </TheWrapper>
  );
};
